/** Fetch helpers for the knowledge graph anchor. Mirrors the
 *  conventions of frontend/src/lib/api.ts (same-origin credentials,
 *  CSRF header on mutations, friendly error mapping). */
import { getCsrfToken } from "../../lib/auth";
import type {
  AnchorsResponse,
  ApiNode,
  NeighborhoodResponse,
  SearchResponse,
} from "./types";

function csrfHeaders(): Record<string, string> {
  const t = getCsrfToken();
  return t ? { "X-CSRF-Token": t } : {};
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) throw new Error(String(parsed.error));
    } catch {
      /* fall through */
    }
    throw new Error(text || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export async function fetchAnchors(limit = 12, includeTest = false): Promise<AnchorsResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (includeTest) params.set("include_test", "1");
  const res = await fetch(`/api/graph/anchors?${params.toString()}`, {
    credentials: "same-origin",
  });
  return jsonOrThrow<AnchorsResponse>(res);
}

export async function fetchNeighborhood(
  nodeId: string,
  depth = 2,
  limit = 100,
  includeTest = false,
): Promise<NeighborhoodResponse> {
  const params = new URLSearchParams({
    node: nodeId,
    depth: String(depth),
    limit: String(limit),
  });
  if (includeTest) params.set("include_test", "1");
  const res = await fetch(`/api/graph/neighborhood?${params.toString()}`, {
    credentials: "same-origin",
  });
  return jsonOrThrow<NeighborhoodResponse>(res);
}

export async function searchGraph(q: string, limit = 20, includeTest = false): Promise<SearchResponse> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  if (includeTest) params.set("include_test", "1");
  const res = await fetch(`/api/graph/search?${params.toString()}`, {
    credentials: "same-origin",
  });
  return jsonOrThrow<SearchResponse>(res);
}

export async function fetchNode(nodeId: string): Promise<ApiNode> {
  const res = await fetch(`/api/graph/nodes/${encodeURIComponent(nodeId)}`, {
    credentials: "same-origin",
  });
  return jsonOrThrow<ApiNode>(res);
}

export async function seedStarterGraph(): Promise<{ seeded: boolean; nodes: number }> {
  const res = await fetch(`/api/graph/seed`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...csrfHeaders() },
    credentials: "same-origin",
    body: "{}",
  });
  return jsonOrThrow<{ seeded: boolean; nodes: number }>(res);
}
